import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import type { CreateContentTestDto } from '@inference.engine/persona-api-client';

import { Loading } from '@/shared/ui';
import { AnalysisContextBanner } from '@/shared/ui';
import { WizardLayout } from '@/shared/ui/layout';
import { PersonaSelector } from '@/shared/ui/PersonaSelector';
import { analysisQuery } from '@/entities/analysis';
import { useCreateContentTest } from '@/entities/content-test';
import { usePersonas } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useMediaUrl } from '@/shared/hooks';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';

import { AssetPicker } from './components/AssetPicker';
import { SetupPreviewPane } from './components/SetupPreviewPane';
import { contentTestSteps, contentTestWizardTitle } from './constants';
import { parsePresetPersonaIds } from './parsePresetPersonaIds';
import {
  NAME_MAX_LENGTH,
  PLATFORMS,
  SETUP_FORM_DEFAULTS,
  type AssetKind,
  type SetupFormValues,
  buildSetupFormSchema,
} from './model/setup-form.schema';
import { DEFAULT_VIDEO_VARIANT_MODEL, VIDEO_VARIANT_MODELS } from './model/video-models';
import { useContentTestAssetUpload } from './model/useContentTestAssetUpload';
import { useGenerateContentTestAsset } from './model/useGenerateContentTestAsset';
import { useTranslation } from 'react-i18next';

const SetupInternal = ({ workspaceId }: { workspaceId: string }) => {
  const { t } = useTranslation('contentTest');
  const { t: tValidation } = useTranslation('validation');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetPersonaIds = useMemo(() => parsePresetPersonaIds(searchParams), [searchParams]);
  // No access check here: the route this page sits on already enforces Content Test access.
  const presetAnalysisId = searchParams.get('analysisId');
  const { data: analysis } = useQuery(
    analysisQuery(presetAnalysisId ?? undefined, { suppressErrorNotification: true }),
  );
  // The analysis still travels when this cannot be read; the run records that the context did
  // not apply, which the results then state. Only the banner needs a brand to name.
  const brandName = analysis?.brandName;
  const videoVariantsEnabled = useBooleanFlagValue('test_content_video_variants', false);
  const { data: personas = [], isPending: personasPending } = usePersonas(workspaceId);
  const createMutation = useCreateContentTest(workspaceId);
  const assetUpload = useContentTestAssetUpload();
  const generateMutation = useGenerateContentTestAsset();

  const formSchema = useMemo(() => buildSetupFormSchema(tValidation), [tValidation]);

  const form = useForm<SetupFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...SETUP_FORM_DEFAULTS,
      personaIds: presetPersonaIds,
    } as SetupFormValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!analysis?.brandName || form.getValues('name')) return;
    const prefilled = t('contentTest.setup.titleFromAnalysis', { brandName: analysis.brandName });
    // A brand name long enough to overflow the title would seed the form invalid, blocking
    // Next on a field nobody filled in. Better to leave it empty for the user to name.
    if (prefilled.length > NAME_MAX_LENGTH) return;
    form.setValue('name', prefilled);
  }, [analysis?.brandName, form, t]);

  const watchedCaption = form.watch('caption');
  const watchedMediaAssetId = form.watch('mediaAssetId');
  const watchedMediaType = form.watch('mediaType');
  const watchedPlatform = form.watch('platform');
  const watchedName = form.watch('name');
  const watchedVariantMediaType = form.watch('variantMediaType');
  const { downloadUrl: acceptedAssetUrl } = useMediaUrl(watchedMediaAssetId, watchedMediaType);

  const handleFileSelected = async (file: File) => {
    try {
      form.setValue('mediaAssetId', undefined, { shouldValidate: true });
      form.setValue('mediaType', undefined);
      const { assetId, kind } = await assetUpload.upload(file);
      form.setValue('mediaAssetId', assetId, { shouldValidate: true });
      form.setValue('mediaType', kind);
    } catch {
      notificationService.showError(t('contentTest.setup.failedToUploadAsset'));
    }
  };

  const handleClearAsset = () => {
    form.setValue('mediaAssetId', undefined, { shouldValidate: true });
    form.setValue('mediaType', undefined);
  };

  // Generation selects the asset directly, mirroring upload. To "regenerate",
  // the user dismisses the selected asset and generates again.
  const handleGenerate = async ({ kind, prompt }: { kind: AssetKind; prompt: string }) => {
    try {
      const result = await generateMutation.mutateAsync({
        kind,
        prompt,
        platform: watchedPlatform,
      });
      form.setValue('mediaAssetId', result.assetId, { shouldValidate: true });
      form.setValue('mediaType', kind);
    } catch {
      notificationService.showError(t('contentTest.setup.failedToGenerateAsset'));
    }
  };

  const onSubmit = async (values: SetupFormValues) => {
    const contentType: CreateContentTestDto['contentType'] =
      values.mediaType ?? (values.mediaAssetId ? 'image' : 'text');
    const payload: CreateContentTestDto = {
      name: values.name.trim(),
      platform: values.platform,
      contentType,
      body: values.caption,
      mediaAssetId: values.mediaAssetId,
      personaIds: values.personaIds,
    };

    if (presetAnalysisId) {
      payload.analysisId = presetAnalysisId;
    }

    // Variant media type is only meaningful behind the flag; when off, omit the
    // fields entirely so the backend applies its image default.
    if (videoVariantsEnabled) {
      payload.variantMediaType = values.variantMediaType;
      if (values.variantMediaType === 'video') {
        payload.variantVideoModel = values.variantVideoModel;
      }
    }

    try {
      const created = await createMutation.mutateAsync(payload);
      notificationService.showSuccess(t('contentTest.setup.contentTestCreated'));
      void navigate(ROUTES.TOOLS_CONTENT_TEST_DETAIL(created.id));
    } catch {
      notificationService.showError(t('contentTest.setup.failedToCreateContentTest'));
    }
  };

  const today = useMemo(() => new Date(), []);

  return (
    <WizardLayout
      configTitle={contentTestWizardTitle()}
      steps={contentTestSteps()}
      activeStep={0}
      onBack={() => void navigate(ROUTES.TOOLS_CONTENT_TEST)}
      configContent={
        <Form {...form}>
          <form
            onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
            className="flex w-full flex-col gap-6"
          >
            {presetAnalysisId && brandName && (
              <AnalysisContextBanner
                label={t('contentTest.setup.brandContextFromAnalysis', { brandName })}
              />
            )}

            <div className="my-2 text-center">
              <h2 className="block pb-4 text-[20px] font-medium leading-6 tracking-[-0.8px] text-foreground">
                {t('contentTest.setup.setupYourContentTest')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('contentTest.setup.chooseAPlatformSetThe')}
              </p>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('contentTest.setup.title')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('contentTest.setup.nameThisTest')}
                      maxLength={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="platform"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('contentTest.setup.selectPlatform')}</FormLabel>
                  <Select
                    value={(field.value as string | undefined) ?? ''}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('contentTest.setup.selectPlatform')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">
                {t('contentTest.setup.selectAsset')}
              </span>
              <AssetPicker
                value={watchedMediaAssetId}
                valueKind={watchedMediaType}
                valueUrl={acceptedAssetUrl}
                isUploading={assetUpload.isUploading}
                onFileSelected={(file) => void handleFileSelected(file)}
                onClear={handleClearAsset}
                isGenerating={generateMutation.isPending}
                canGenerate={!!watchedPlatform}
                onGenerate={handleGenerate}
              />
            </div>

            <FormField
              control={form.control}
              name="caption"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('contentTest.setup.captionText')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t('contentTest.setup.writeACaptionOrText')}
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {videoVariantsEnabled && (
              <FormField
                control={form.control}
                name="variantMediaType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contentTest.setup.variantFormat')}</FormLabel>
                    <FormControl>
                      <ToggleGroup
                        type="single"
                        value={field.value}
                        onValueChange={(value) => {
                          if (!value) return;
                          field.onChange(value);
                          // Preselect a model when switching to video so the
                          // form is immediately valid; leave any prior choice.
                          if (value === 'video' && !form.getValues('variantVideoModel')) {
                            form.setValue('variantVideoModel', DEFAULT_VIDEO_VARIANT_MODEL, {
                              shouldValidate: true,
                            });
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="grid w-full grid-cols-2"
                      >
                        {/* eslint-disable-next-line i18next/no-literal-string -- media kind wire value */}
                        <ToggleGroupItem value="image" className="py-2">
                          {t('contentTest.setup.image')}
                        </ToggleGroupItem>
                        {/* eslint-disable-next-line i18next/no-literal-string -- media kind wire value */}
                        <ToggleGroupItem value="video" className="py-2">
                          {t('contentTest.setup.video')}
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {videoVariantsEnabled && watchedVariantMediaType === 'video' && (
              <FormField
                control={form.control}
                name="variantVideoModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contentTest.setup.videoModel')}</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('contentTest.setup.selectAVideoModel')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {VIDEO_VARIANT_MODELS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                            <span className="ml-2 text-muted-foreground">{m.provider}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="personaIds"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>
                    {t('contentTest.setup.personas')} <span className="text-destructive">*</span>
                  </FormLabel>
                  <PersonaSelector
                    value={field.value}
                    onChange={field.onChange}
                    personas={personas}
                    error={fieldState.error?.message}
                    disabled={personasPending}
                  />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  !form.formState.isValid ||
                  createMutation.isPending ||
                  assetUpload.isUploading ||
                  generateMutation.isPending
                }
              >
                {t('contentTest.setup.next')}
              </Button>
            </div>
          </form>
        </Form>
      }
    >
      <SetupPreviewPane
        caption={watchedCaption}
        mediaAssetId={watchedMediaAssetId}
        mediaType={watchedMediaType}
        date={today}
        title={watchedName}
        platform={watchedPlatform}
      />
    </WizardLayout>
  );
};

export const Setup = () => {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) return <Loading />;

  return <SetupInternal workspaceId={workspaceId} />;
};

Setup.displayName = 'Setup';
